import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { apiService } from '../services/api';

/**
 * Configurações gerais do billing (vover-billing / billingconfigs).
 * - dueReminderDays: lembretes e avisos no app (0 = no dia do vencimento)
 * - overdueReminderDaysAfter: lembretes recorrentes após vencimento (webhook `payment.overdue_reminder`)
 * - nextPeriodPaymentOpensDays: janela para pagar fatura de período futuro (vover-app)
 */
const GeneralSystemConfigPage: React.FC = () => {
  const [dueReminderDaysInput, setDueReminderDaysInput] = useState('3, 1');
  const [overdueReminderDaysAfterInput, setOverdueReminderDaysAfterInput] = useState('1, 3, 7, 14');
  const [overdueRemindersEnabled, setOverdueRemindersEnabled] = useState(true);
  const [nextPeriodOpensInput, setNextPeriodOpensInput] = useState('7');
  const [reminderFieldError, setReminderFieldError] = useState<string | null>(null);
  const [overdueFieldError, setOverdueFieldError] = useState<string | null>(null);
  const [opensFieldError, setOpensFieldError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [signupNotifyEnabled, setSignupNotifyEnabled] = useState(false);
  const [signupNotifyEmailsInput, setSignupNotifyEmailsInput] = useState('');
  const [signupSaving, setSignupSaving] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [res, sys] = await Promise.all([
          apiService.getBillingGlobalConfig(),
          apiService.getSystemConfig()
        ]);
        const data = ((res.data as { data?: unknown })?.data ?? res.data) as {
          dueReminderDays?: number[];
          overdueReminderDaysAfter?: number[];
          nextPeriodPaymentOpensDays?: number;
        };
        if (cancelled) return;
        const raw = data?.dueReminderDays;
        if (Array.isArray(raw) && raw.length > 0) {
          setDueReminderDaysInput(raw.join(', '));
        }
        const oa = data?.overdueReminderDaysAfter;
        if (Array.isArray(oa)) {
          if (oa.length === 0) {
            setOverdueRemindersEnabled(false);
            setOverdueReminderDaysAfterInput('1, 3, 7, 14');
          } else {
            setOverdueRemindersEnabled(true);
            setOverdueReminderDaysAfterInput(oa.join(', '));
          }
        }
        const opens = data?.nextPeriodPaymentOpensDays;
        if (typeof opens === 'number' && opens >= 1) {
          setNextPeriodOpensInput(String(Math.min(365, Math.floor(opens))));
        }

        const sysData = (sys.data?.data ?? sys.data) as {
          notifyOnNewTenantSignup?: boolean;
          notifyOnNewTenantSignupEmails?: string[];
        };
        setSignupNotifyEnabled(Boolean(sysData?.notifyOnNewTenantSignup));
        const em = Array.isArray(sysData?.notifyOnNewTenantSignupEmails)
          ? sysData.notifyOnNewTenantSignupEmails
          : [];
        setSignupNotifyEmailsInput(em.join(', '));
      } catch {
        if (!cancelled)
          setMessage({ type: 'error', text: 'Não foi possível carregar a configuração.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const parseReminderDays = (s: string): number[] | null => {
    const parts = s
      .split(/[,;\s]+/)
      .map(x => x.trim())
      .filter(Boolean);
    const nums = parts.map(p => parseInt(p, 10));
    if (
      parts.length === 0 ||
      nums.some(n => !Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > 365)
    ) {
      return null;
    }
    return [...new Set(nums)].sort((a, b) => b - a);
  };

  const parseOverdueReminderDaysAfter = (s: string): number[] | null => {
    const parts = s
      .split(/[,;\s]+/)
      .map(x => x.trim())
      .filter(Boolean);
    const nums = parts.map(p => parseInt(p, 10));
    if (
      parts.length === 0 ||
      nums.some(n => !Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 365)
    ) {
      return null;
    }
    return [...new Set(nums)].sort((a, b) => a - b);
  };

  const parseOpensDays = (s: string): number | null => {
    const n = parseInt(String(s).trim(), 10);
    if (!Number.isFinite(n) || n < 1 || n > 365) return null;
    return n;
  };

  const parseEmails = (s: string): string[] | null => {
    const parts = s
      .split(/[,;\s]+/)
      .map(x => x.trim().toLowerCase())
      .filter(Boolean);
    if (parts.length === 0) return [];
    const valid = parts.filter(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (valid.length !== parts.length) return null;
    return Array.from(new Set(valid));
  };

  const handleSave = async () => {
    setMessage(null);
    setReminderFieldError(null);
    setOverdueFieldError(null);
    setOpensFieldError(null);
    const days = parseReminderDays(dueReminderDaysInput);
    const opens = parseOpensDays(nextPeriodOpensInput);
    if (!days) {
      setReminderFieldError(
        'Use números inteiros entre 0 e 365, separados por vírgula (ex.: 3, 1 ou 3, 1, 0 para incluir o dia do vencimento).'
      );
      return;
    }
    let overdueAfter: number[];
    if (overdueRemindersEnabled) {
      const parsed = parseOverdueReminderDaysAfter(overdueReminderDaysAfterInput);
      if (!parsed) {
        setOverdueFieldError(
          'Lembretes após vencimento: use inteiros entre 1 e 365, separados por vírgula (ex.: 1, 3, 7, 14).'
        );
        return;
      }
      overdueAfter = parsed;
    } else {
      overdueAfter = [];
    }
    if (opens == null) {
      setOpensFieldError('Informe um número inteiro entre 1 e 365.');
      return;
    }
    setSaving(true);
    try {
      await apiService.updateBillingGlobalConfig({
        dueReminderDays: days,
        overdueReminderDaysAfter: overdueAfter,
        nextPeriodPaymentOpensDays: opens
      });
      setDueReminderDaysInput(days.join(', '));
      if (overdueRemindersEnabled) {
        setOverdueReminderDaysAfterInput(overdueAfter.join(', '));
      }
      setNextPeriodOpensInput(String(opens));
      setMessage({
        type: 'success',
        text: 'Salvo. Lembretes (pré e pós-vencimento) e a janela de pagamento antecipado foram atualizados.'
      });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Falha ao salvar.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSignupNotify = async () => {
    setSignupError(null);
    const emails = parseEmails(signupNotifyEmailsInput);
    if (emails == null) {
      setSignupError('Informe emails válidos, separados por vírgula ou ponto e vírgula.');
      return;
    }
    setSignupSaving(true);
    try {
      await apiService.updateSystemConfig({
        notifyOnNewTenantSignup: signupNotifyEnabled,
        notifyOnNewTenantSignupEmails: emails
      });
      setSignupNotifyEmailsInput(emails.join(', '));
      setMessage({ type: 'success', text: 'Configuração de notificação de cadastro salva.' });
    } catch (e: any) {
      setSignupError(e?.response?.data?.message || 'Falha ao salvar notificação de cadastro.');
    } finally {
      setSignupSaving(false);
    }
  };

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={20} />
            <Typography>Carregando configurações gerais…</Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      <Card
        variant="outlined"
        sx={{
          borderColor: 'primary.light',
          bgcolor: 'action.hover'
        }}
      >
        <CardContent>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Geral (Sistema)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Parâmetros globais que afetam faturamento, notificações automáticas e cadastros. Use
                esta aba para ajustar regras operacionais compartilhadas pelos produtos.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label="Escopo global" color="primary" size="small" />
              <Chip label="Fonte: billingconfigs + system_config" variant="outlined" size="small" />
              <Chip label="Sem tarefa manual após salvar" variant="outlined" size="small" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {message && (
        <Alert severity={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Regras de cobrança no app
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Define quando o tenant vê avisos, recebe lembretes e pode pagar faturas futuras.
                Essas regras são lidas pelo vover-billing e refletidas no produto via webhooks.
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Antes do vencimento
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Dias antes do vencimento em que o billing notifica o produto. O maior valor
                      também inicia o aviso visual na área financeira.
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Use <code>0</code> para incluir o dia do vencimento. Duplicados são removidos.
                    </Alert>
                    <TextField
                      label="Dias antes do vencimento"
                      value={dueReminderDaysInput}
                      onChange={e => setDueReminderDaysInput(e.target.value)}
                      fullWidth
                      error={Boolean(reminderFieldError)}
                      helperText={
                        reminderFieldError || 'Ex.: 3, 1 ou 3, 1, 0 (0 = no dia do vencimento)'
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Após o vencimento
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Reenvio recorrente de <code>payment.overdue_reminder</code> para e-mail e push
                      dos responsáveis financeiros.
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={overdueRemindersEnabled}
                          onChange={e => setOverdueRemindersEnabled(e.target.checked)}
                        />
                      }
                      label="Enviar lembretes recorrentes"
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      label="Dias após o vencimento"
                      value={overdueReminderDaysAfterInput}
                      onChange={e => setOverdueReminderDaysAfterInput(e.target.value)}
                      fullWidth
                      disabled={!overdueRemindersEnabled}
                      error={Boolean(overdueFieldError)}
                      helperText={
                        overdueFieldError ||
                        (overdueRemindersEnabled
                          ? 'Ex.: 1, 3, 7, 14. Um disparo por dia configurado.'
                          : 'Desativado: nenhum lembrete pós-vencimento será agendado.')
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                      Pagamento antecipado
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Quantos dias antes do vencimento uma fatura de período futuro pode gerar
                      boleto ou PIX no app.
                    </Typography>
                    <TextField
                      label="Janela para liberar pagamento"
                      value={nextPeriodOpensInput}
                      onChange={e => setNextPeriodOpensInput(e.target.value)}
                      fullWidth
                      type="number"
                      inputProps={{ min: 1, max: 365 }}
                      error={Boolean(opensFieldError)}
                      helperText={
                        opensFieldError ||
                        'Ex.: 7 — libera quando faltarem 7 dias ou menos para o vencimento'
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando…' : 'Salvar regras de cobrança'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Notificação de novo cadastro
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Envia um e-mail para o time interno sempre que um novo tenant se cadastra. Útil para
                acompanhamento comercial, suporte inicial e validação de onboarding.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={signupNotifyEnabled}
                      onChange={e => setSignupNotifyEnabled(e.target.checked)}
                    />
                  }
                  label="Notificar quando um novo tenant se cadastrar"
                />
                <TextField
                  label="Emails de destino"
                  value={signupNotifyEmailsInput}
                  onChange={e => setSignupNotifyEmailsInput(e.target.value)}
                  fullWidth
                  error={Boolean(signupError)}
                  helperText={
                    signupError || 'Ex.: financeiro@empresa.com.br, suporte@empresa.com.br'
                  }
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={handleSaveSignupNotify}
                    disabled={signupSaving}
                  >
                    {signupSaving ? 'Salvando…' : 'Salvar notificação'}
                  </Button>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default GeneralSystemConfigPage;
